import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import HowToRegIcon from "@mui/icons-material/HowToRegOutlined";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import Swal from "sweetalert2";
import StatusChip from "components/StatusChip";
import { API_BASE } from "config/api";

const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const ChildCell = ({ row }) => (
  <Box textAlign="left">
    <Typography variant="button" fontWeight={600} display="block">
      {row.original.name}
    </Typography>
    {row.original.guardianName && (
      <Typography variant="caption" color="text.secondary">
        Guardian: {row.original.guardianName}
      </Typography>
    )}
  </Box>
);
ChildCell.propTypes = { row: PropTypes.shape({ original: PropTypes.object }).isRequired };

const ChildActionsCell = ({ row, handleEdit, handleDelete, handleCheckIn }) => (
  <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
    <Button
      size="small"
      variant="outlined"
      color="success"
      startIcon={<HowToRegIcon fontSize="small" />}
      onClick={() => handleCheckIn(row.original)}
    >
      Check In
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={() => handleEdit(row.original)}
    >
      Edit
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={() => handleDelete(row.original)}
    >
      Delete
    </Button>
  </Stack>
);
ChildActionsCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleCheckIn: PropTypes.func.isRequired,
};

const CodeCell = ({ value }) => (
  <Typography variant="button" fontWeight={700} letterSpacing={2}>
    {value}
  </Typography>
);
CodeCell.propTypes = { value: PropTypes.string.isRequired };

const SessionStatusCell = ({ value }) => (
  <StatusChip
    value={value === "active" ? "Checked In" : "Picked Up"}
    color={value === "active" ? "success" : "default"}
  />
);
SessionStatusCell.propTypes = { value: PropTypes.string.isRequired };

const SessionActionsCell = ({ row, handleCheckOut }) =>
  row.original.status === "active" ? (
    <Button
      size="small"
      variant="contained"
      color="warning"
      startIcon={<LogoutIcon fontSize="small" />}
      onClick={() => handleCheckOut(row.original)}
    >
      Check Out
    </Button>
  ) : (
    <Typography variant="caption" color="text.secondary">
      —
    </Typography>
  );
SessionActionsCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
  handleCheckOut: PropTypes.func.isRequired,
};

const emptyChildForm = () => ({
  id: null,
  first_name: "",
  surname: "",
  guardian_member_id: null,
  guardianLabel: "",
  date_of_birth: "",
  allergies: "",
  notes: "",
});

export default function useChildCheckinData() {
  const { church } = useAuth();

  const [children, setChildren] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sessions, setSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);

  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [newCheckin, setNewCheckin] = useState(null);
  const [checkoutCode, setCheckoutCode] = useState("");

  const [form, setForm] = useState(emptyChildForm());
  const [isSaving, setIsSaving] = useState(false);

  const [guardianSearch, setGuardianSearch] = useState("");
  const [guardianResults, setGuardianResults] = useState([]);
  const [guardianSelectedLabel, setGuardianSelectedLabel] = useState("");

  const authHeaders = () => {
    const token = localStorage.getItem("authToken");
    return token ? { Accept: "application/json", Authorization: `Bearer ${token}` } : null;
  };

  const handleEdit = useCallback((row) => {
    const raw = row.originalData || {};
    const guardianLabel = raw.guardian ? `${raw.guardian.first_name} ${raw.guardian.surname}` : "";
    setForm({
      id: row.id,
      first_name: raw.first_name || "",
      surname: raw.surname || "",
      guardian_member_id: raw.guardian_member_id || null,
      guardianLabel,
      date_of_birth: raw.date_of_birth ? raw.date_of_birth.split("T")[0] : "",
      allergies: raw.allergies || "",
      notes: raw.notes || "",
    });
    setGuardianSearch(guardianLabel);
    setGuardianSelectedLabel(guardianLabel);
    setGuardianResults([]);
    setEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((row) => {
    setSelectedChild(row);
    setDeleteModalOpen(true);
  }, []);

  const childColumns = [
    { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
    { Header: "Child", accessor: "name", align: "left", width: "30%", Cell: ChildCell },
    { Header: "Allergies", accessor: "allergies", align: "left", width: "20%" },
    {
      Header: "Actions",
      accessor: "actions",
      align: "center",
      width: "35%",
      Cell: (props) => (
        <ChildActionsCell
          {...props}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleCheckIn={handleCheckIn}
        />
      ),
    },
  ];

  const sessionColumns = [
    { Header: "Child", accessor: "childName", align: "left", width: "25%" },
    { Header: "Code", accessor: "securityCode", align: "center", width: "15%", Cell: CodeCell },
    { Header: "Checked In", accessor: "checkedInLabel", align: "left", width: "20%" },
    {
      Header: "Status",
      accessor: "status",
      align: "center",
      width: "15%",
      Cell: SessionStatusCell,
    },
    {
      Header: "Actions",
      accessor: "actions",
      align: "center",
      width: "25%",
      Cell: (props) => <SessionActionsCell {...props} handleCheckOut={handleCheckOut} />,
    },
  ];

  const fetchChildren = useCallback(async () => {
    if (!church?.id) return;
    setIsLoading(true);
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/children`, { headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      const list = result.data || [];
      setChildren(
        list.map((c, index) => ({
          id: c.id,
          serial: index + 1,
          name: `${c.first_name} ${c.surname}`,
          guardianName: c.guardian ? `${c.guardian.first_name} ${c.guardian.surname}` : null,
          allergies: c.allergies || "—",
          originalData: c,
        }))
      );
      setError(null);
    } catch (e) {
      console.error("Fetch children failed:", e);
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [church?.id]);

  const fetchSessions = useCallback(async () => {
    if (!church?.id) return;
    setIsLoadingSessions(true);
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/child-checkins`, { headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      const list = result.data || [];
      setSessions(
        list.map((s) => ({
          id: s.id,
          childName: s.child ? `${s.child.first_name} ${s.child.surname}` : "—",
          securityCode: s.security_code,
          checkedInLabel: new Date(s.checked_in_at).toLocaleTimeString(),
          status: s.checked_out_at ? "picked_up" : "active",
          originalData: s,
        }))
      );
    } catch (e) {
      console.error("Fetch child sessions failed:", e);
    } finally {
      setIsLoadingSessions(false);
    }
  }, [church?.id]);

  useEffect(() => {
    fetchChildren();
    fetchSessions();
  }, [fetchChildren, fetchSessions]);

  const searchGuardians = async (query) => {
    if (!church?.id || !query.trim()) {
      setGuardianResults([]);
      return;
    }
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(
        `${API_BASE}/members/${church.id}/search?query=${encodeURIComponent(query)}`,
        { headers }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      setGuardianResults(result.data || []);
    } catch (e) {
      console.error("Guardian search failed:", e);
      setGuardianResults([]);
    }
  };

  useEffect(() => {
    if (!guardianSearch.trim() || guardianSearch === guardianSelectedLabel) {
      setGuardianResults([]);
      return;
    }
    const timer = setTimeout(() => searchGuardians(guardianSearch), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [guardianSearch]);

  const handleSelectGuardian = (member) => {
    const label = `${member.first_name} ${member.surname}`;
    setForm((prev) => ({ ...prev, guardian_member_id: member.id, guardianLabel: label }));
    setGuardianSearch(label);
    setGuardianSelectedLabel(label);
    setGuardianResults([]);
  };

  const handleAdd = () => {
    setForm(emptyChildForm());
    setGuardianSearch("");
    setGuardianSelectedLabel("");
    setGuardianResults([]);
    setEditModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (!form.first_name.trim() || !form.surname.trim())
        throw new Error("First name and surname are required.");
      setIsSaving(true);
      const headers = authHeaders();
      if (!headers) return;

      const url = form.id ? `${API_BASE}/children/${form.id}` : `${API_BASE}/children`;
      const method = form.id ? "PUT" : "POST";

      const payload = {
        first_name: form.first_name,
        surname: form.surname,
        guardian_member_id: form.guardian_member_id || null,
        date_of_birth: form.date_of_birth || null,
        allergies: form.allergies || null,
        notes: form.notes || null,
      };

      const response = await fetch(url, {
        method,
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: `Child profile ${form.id ? "updated" : "created"}`,
        icon: "success",
      });
      fetchChildren();
      setEditModalOpen(false);
    } catch (e) {
      Swal.fire({ title: "Error!", text: e.message, icon: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/children/${selectedChild.id}`, {
        method: "DELETE",
        headers,
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      Swal.fire({ title: "Success!", text: "Child profile deleted", icon: "success" });
      fetchChildren();
      setDeleteModalOpen(false);
    } catch (e) {
      Swal.fire({ title: "Error!", text: e.message, icon: "error" });
    }
  };

  function handleCheckIn(row) {
    (async () => {
      try {
        const headers = authHeaders();
        if (!headers) return;
        const response = await fetch(`${API_BASE}/child-checkins`, {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ child_id: row.id }),
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.message || `HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        setNewCheckin(result.data);
        setCodeModalOpen(true);
        fetchSessions();
      } catch (e) {
        Swal.fire({ title: "Error!", text: e.message, icon: "error" });
      }
    })();
  }

  function handleCheckOut(row) {
    setSelectedSession(row.originalData);
    setCheckoutCode("");
    setCheckoutModalOpen(true);
  }

  const handleCheckoutConfirm = async () => {
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/child-checkins/${selectedSession.id}/checkout`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ security_code: checkoutCode }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }
      Swal.fire({ title: "Checked out!", text: "Child has been picked up.", icon: "success" });
      setCheckoutModalOpen(false);
      fetchSessions();
    } catch (e) {
      Swal.fire({ title: "Error!", text: e.message, icon: "error" });
    }
  };

  return {
    childColumns,
    children,
    isLoading,
    error,
    sessionColumns,
    sessions,
    isLoadingSessions,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    codeModalOpen,
    setCodeModalOpen,
    checkoutModalOpen,
    setCheckoutModalOpen,
    selectedChild,
    newCheckin,
    checkoutCode,
    setCheckoutCode,
    form,
    setForm,
    handleAdd,
    handleSubmit,
    handleDeleteConfirm,
    isSaving,
    guardianSearch,
    setGuardianSearch,
    guardianResults,
    handleSelectGuardian,
    handleCheckoutConfirm,
  };
}
